import { useCallback, useRef } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Tooltip } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';

import ClipboardListOutline from '@/assets/icons/ClipboardListOutline';
import Button from '@/components/Buttons/Button';
import type { SymbolKey } from '@/entities/symbol';
import AccordionSection from '@/features/DataHub/components/AccordionSection';
import {
  DATAHUB_ITEM_DETAILS_MAX_ROWS,
  DATAHUB_LIST_ROW_HEIGHT,
  DATAHUB_ROW_HEIGHT
} from '@/features/DataHub/constants/ui';
import type { CatalogSymbolItem } from '@/features/DataHub/hooks/useCatalogSymbolItems';
import { useCatalogSymbolItems } from '@/features/DataHub/hooks/useCatalogSymbolItems';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';

const COLUMN_WIDTHS = {
  alias: '80px',
  code: '90px',
  currency: '75px',
  units: '60px'
};

const ItemsHeader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        fontSize: '12px',
        gap: '16px'
      }}
    >
      <Box sx={{ width: COLUMN_WIDTHS.alias }}>Alias</Box>
      <Box sx={{ width: COLUMN_WIDTHS.code }}>GX Code</Box>
      <Box sx={{ width: COLUMN_WIDTHS.currency }}>Currency</Box>
      <Box sx={{ width: COLUMN_WIDTHS.units }}>Unit</Box>
      <Box sx={{ flex: 1 }}>Short Title</Box>
    </Box>
  );
};

export const ItemRow = ({ item }: { item: CatalogSymbolItem }) => {
  const { removeSymbolFromSelection } = useSelectionFunctions();
  const handleClick = useCallback(() => {
    removeSymbolFromSelection(item.symbol);
  }, [item.symbol, removeSymbolFromSelection]);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        height: DATAHUB_ROW_HEIGHT,
        minHeight: DATAHUB_ROW_HEIGHT,
        borderWidth: 1,
        borderColor: 'text.primary',
        borderStyle: 'solid',
        flex: 1,
        fontSize: '14px',
        gap: 2
      }}
    >
      <Box sx={{ width: COLUMN_WIDTHS.alias }}>{item.alias}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.code }}>{item.code}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.currency }}>{item.currency}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.units }}>{item.units}</Box>
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        <Tooltip title={item.title}>
          <Box component='span'>{item.title}</Box>
        </Tooltip>
      </Box>
      <Button
        sx={{
          width: '32px',
          height: '32px',
          backgroundColor: 'gx.actionBarButtonTile',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={handleClick}
      >
        <DeleteOutlineIcon
          sx={{
            mr: '0 !important'
          }}
        />
      </Button>
    </Box>
  );
};

export const ItemDetails = ({ symbols }: { symbols: SymbolKey[] }) => {
  const { items } = useCatalogSymbolItems(symbols);
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => DATAHUB_LIST_ROW_HEIGHT,
    gap: 8
  });

  return (
    <AccordionSection
      icon={<ClipboardListOutline />}
      title='Item details'
    >
      <ItemsHeader />
      <Box
        ref={parentRef}
        sx={{
          maxHeight: DATAHUB_ITEM_DETAILS_MAX_ROWS * DATAHUB_LIST_ROW_HEIGHT,
          overflow: 'auto'
        }}
      >
        <Box
          sx={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative'
          }}
        >
          {rowVirtualizer.getVirtualItems().map(virtualRow => (
            <Box
              key={virtualRow.key}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`
              }}
            >
              <ItemRow item={items[virtualRow.index]} />
            </Box>
          ))}
        </Box>
      </Box>
    </AccordionSection>
  );
};
